// Pure helpers for the embed widget's notification bell. Widget viewers are
// anonymous, so "seen" state lives only in the viewer's browser (localStorage
// inside the iframe). Every storage access is guarded: storage can be blocked
// or cleared (private windows, third-party storage rules) and the widget must
// still render - the worst case is the badge reappearing.

const SEEN_KEY_PREFIX = "gew:seen:";
const MAX_SEEN_IDS = 200;

export const loadSeenIds = (widgetKey, storage = getStorage()) => {
  try {
    const raw = storage?.getItem(SEEN_KEY_PREFIX + widgetKey);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed.map(String) : [];
  } catch {
    return [];
  }
};

export const saveSeenIds = (widgetKey, ids, storage = getStorage()) => {
  try {
    const unique = Array.from(new Set(ids.map(String))).slice(-MAX_SEEN_IDS);
    storage?.setItem(SEEN_KEY_PREFIX + widgetKey, JSON.stringify(unique));
  } catch {
    // Storage unavailable - badge simply reappears next load.
  }
};

export const getUnseenIds = (notifications, seenIds) => {
  const seen = new Set(seenIds.map(String));
  return (notifications || [])
    .filter((n) => !seen.has(String(n.id)))
    .map((n) => String(n.id));
};

export const countUnseen = (notifications, seenIds) =>
  getUnseenIds(notifications, seenIds).length;

// Destinations come from the API as relative paths. Re-check here so a bad
// value can never turn into an off-site link: it must be a single-slash path.
export const isSafePath = (path) =>
  typeof path === "string" && path.startsWith("/") && !path.startsWith("//");

// Resolving against the origin (instead of concatenating) means a path that
// already carries a query string still produces a valid URL, and the origin
// check below is enforced rather than assumed.
export const buildNotificationUrl = (origin, notification) => {
  if (!origin || !notification || !isSafePath(notification.path)) return null;
  try {
    const url = new URL(notification.path, origin);
    if (url.origin !== new URL(origin).origin) return null;
    url.searchParams.set("utm_source", "widget");
    url.searchParams.set("utm_medium", "notification");
    url.searchParams.set("utm_campaign", `widget_broadcast_${notification.id}`);
    return url.toString();
  } catch {
    return null;
  }
};

// What opening the bell does to the seen state: everything listed becomes seen,
// and only the ids that were unseen are reported as views (and highlighted).
export const planBellOpen = (notifications, seenIds) => {
  const unseen = getUnseenIds(notifications, seenIds);
  const seen = Array.from(
    new Set([
      ...seenIds.map(String),
      ...notifications.map((n) => String(n.id)),
    ]),
  );
  return { unseen, seen };
};

// Kill switch: set featureFlags.widgetNotifications to false in a tenant config
// (or in default.json for every tenant) to turn the bell's notifications off.
// Absent means on.
export const areWidgetNotificationsEnabled = (themeConfig) =>
  themeConfig?.featureFlags?.widgetNotifications !== false;

function getStorage() {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}
